import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Button } from "antd";
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
  getPlaidApiBaseUrl,
} from "@/api/custom";
import {
  buildPlaidAppRedirectUrl,
  buildPlaidPublicRedirectUrl,
  consumePlaidRedirectQueryParams,
  currentLocationMatchesPlaidReturnPath,
  derivePlaidRedirectOutcome,
} from "./plaid-redirect-utils";
import { useAppDispatch } from "@/store/hooks";
import {
  // setGlobalPlaidData,
  // setPlaidError,
  // setAggregatedData,
  setConnectionError,
} from "@/store/features/plaid/plaidSlice";
import { fetchPlaidWealthSnapshot } from "@/store/features/plaid/plaid-thunks";
import "./plaid-link-modal.scss";

interface PlaidLinkModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  returnPath?: string;
}

const PlaidLinkModal: React.FC<PlaidLinkModalProps> = ({
  visible,
  onClose,
  onSuccess,
  returnPath,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"initial" | "linking" | "success" | "error">(
    "initial",
  );
  const dispatch = useAppDispatch();
  // Each Get started is one attempt; Cancel ends it, so a token that
  // arrives afterwards doesn't send the investor to Plaid.
  const attemptRef = useRef(0);

  const handleStartLinking = async () => {
    const attempt = ++attemptRef.current;
    try {
      setLoading(true);
      setError(null);
      setStep("linking");

      // Create link token
      const linkTokenResponse = await createPlaidLinkToken();
      const linkToken = linkTokenResponse.link_token;

      // Always use the same Plaid API endpoint that the app is configured to call.
      const plaidApiBaseUrl = await getPlaidApiBaseUrl();
      if (attempt !== attemptRef.current) {
        return;
      }
      const redirectUrl = buildPlaidAppRedirectUrl(
        window.location.origin,
        returnPath,
      );
      const plaidRedirectUrl = buildPlaidPublicRedirectUrl(
        plaidApiBaseUrl,
        linkToken,
        redirectUrl,
      );

      // Redirect in the same window (no popup)
      window.location.assign(plaidRedirectUrl);
    } catch (err: unknown) {
      console.error("Error starting Plaid linking:", err);
      if (attempt !== attemptRef.current) {
        return;
      }
      setStep("error");
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to start bank connection process. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handlePlaidSuccess = useCallback(
    async (publicToken?: string | null) => {
      try {
        setLoading(true);
        setError(null);
        setStep("linking");

        // Exchange public token for access token
        if (publicToken) {
          await exchangePlaidPublicToken(publicToken);
        }

        // Refresh Plaid data
        // const plaidData = await getPlaidAggregatedData();
        await dispatch(fetchPlaidWealthSnapshot({ range: "ALL" })).unwrap();
        dispatch(setConnectionError(null));

        setStep("success");
        setLoading(false);
      } catch (err: unknown) {
        console.error("Error exchanging Plaid token:", err);
        setStep("error");
        setError(
          err instanceof Error
            ? err.message
            : "Failed to complete bank connection. Please try again.",
        );
        setLoading(false);
      }
    },
    [dispatch],
  );

  // Check for Plaid success URL parameters
  useEffect(() => {
    if (
      !currentLocationMatchesPlaidReturnPath(
        window.location.pathname,
        returnPath,
      )
    ) {
      return;
    }
    const redirectParams = consumePlaidRedirectQueryParams();
    const outcome = derivePlaidRedirectOutcome(redirectParams);
    if (outcome.kind === "none") {
      return;
    }
    if (outcome.kind === "cancel") {
      setStep("error");
      setError(outcome.message);
      setLoading(false);
      return;
    }
    if (outcome.kind === "error") {
      setStep("error");
      setError(outcome.message);
      setLoading(false);
      return;
    }
    if (outcome.kind === "success") {
      void handlePlaidSuccess(outcome.publicToken);
    }
  }, [handlePlaidSuccess, returnPath]);

  const handleRetry = () => {
    setStep("initial");
    setError(null);
    setLoading(false);
  };

  const handleCancel = () => {
    attemptRef.current += 1;
    setStep("initial");
    setError(null);
    setLoading(false);
    onClose();
  };

  const handleDone = () => {
    setStep("initial");
    onSuccess();
    onClose();
  };

  const renderContent = () => {
    switch (step) {
      case "initial":
        return (
          <div className="plaid-link-body">
            <h3>Connect your bank accounts</h3>
            <div className="plaid-link-label">What happens next</div>
            <ol className="plaid-link-steps">
              <li>
                <b aria-hidden="true">1</b>You&apos;ll go to Plaid&apos;s secure page
              </li>
              <li>
                <b aria-hidden="true">2</b>Pick your bank and sign in there
              </li>
              <li>
                <b aria-hidden="true">3</b>Choose which accounts to connect
              </li>
              <li>
                <b aria-hidden="true">4</b>Come back here to see them
              </li>
            </ol>
            <div className="plaid-link-foot">
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" onClick={handleStartLinking} loading={loading}>
                Get started
              </Button>
            </div>
          </div>
        );

      case "linking":
        return (
          <div className="plaid-link-body is-center" role="status">
            <span className="plaid-link-spin" aria-hidden="true" />
            <h3>Connecting your accounts</h3>
            <p>Finish connecting in the Plaid window. This page updates when you&apos;re done.</p>
            <Button onClick={handleCancel}>Cancel</Button>
          </div>
        );

      case "success":
        return (
          <div className="plaid-link-body is-center" role="status">
            <span className="plaid-link-badge is-ok" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M5 12l5 5L19 7" />
              </svg>
            </span>
            <h3>Accounts connected</h3>
            <p>Your bank accounts now show in Linked accounts.</p>
            <Button type="primary" className="plaid-link-done" onClick={handleDone}>
              Done
            </Button>
          </div>
        );

      case "error":
        return (
          <div className="plaid-link-body is-center" role="alert">
            <span className="plaid-link-badge is-warn" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v5M12 16.5v.01" />
              </svg>
            </span>
            <h3>Connection failed</h3>
            <p>{error || "Something went wrong connecting your bank. Please try again."}</p>
            <div className="plaid-link-foot is-center">
              <Button onClick={handleCancel}>Close</Button>
              <Button type="primary" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Only the first step shows a title bar; the others are a centred status,
  // so their title stays for screen readers but is hidden on screen.
  const isIntro = step === "initial";
  const titles = {
    initial: "Link an account",
    linking: "Connecting your accounts",
    success: "Accounts connected",
    error: "Connection failed",
  };

  return (
    <Modal
      className={`plaid-link-modal${isIntro ? "" : " is-status"}`}
      title={titles[step]}
      open={visible}
      onCancel={step === "success" ? handleDone : handleCancel}
      closable={isIntro}
      footer={null}
      width={480}
      centered
      destroyOnHidden
    >
      {renderContent()}
    </Modal>
  );
};

export default PlaidLinkModal;
